import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Exact-print jabla: slideshow arrows navigate purchasable designs, not just photos.
    this.addEventListener('click', this.#handleDesignArrowClick, true);

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleDesignArrowClick, true);
    this.#controller.abort();
  }

  /**
   * Keep previous/next arrows synchronized with the selected design variant.
   * The native radio change flow updates the gallery, URL, price and cart variant.
   * @param {MouseEvent} event
   */
  #handleDesignArrowClick = (event) => {
    const control = event.target instanceof Element
      ? event.target.closest('slideshow-arrows .slideshow-control--next, slideshow-arrows .slideshow-control--previous')
      : null;
    if (!control) return;

    const section = this.closest('.shopify-section');
    if (!section) return;
    const picker = section.querySelector('variant-picker[data-product-handle^="muslin-front-open-button-jabla-"]');
    if (!picker) return;
    const radios = Array.from(picker.querySelectorAll('.lb-design-image-picker input[type="radio"]'));
    if (radios.length < 2) return;

    // Intercept before the slideshow's click handler so one click does not perform two transitions.
    event.preventDefault();
    event.stopImmediatePropagation();
    const current = Math.max(0, radios.findIndex((radio) => radio.checked));
    const direction = control.classList.contains('slideshow-control--next') ? 1 : -1;
    // Skip disabled designs instead of leaving navigation apparently broken.
    const available = (radio) => !radio.disabled && radio.getAttribute('aria-disabled') !== 'true' && radio.closest('[aria-disabled="true"]') === null;
    for (let step = 1; step <= radios.length; step += 1) {
      const target = radios[(current + direction * step + radios.length * 2) % radios.length];
      if (target && available(target)) {
        if (target !== radios[current]) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        break;
      }
    }
  };

  /**
   * A fingerprint of the photos in a gallery, in order. Two galleries with the same fingerprint show the same slides.
   * @param {Element} gallery
   * @returns {string}
   */
  #mediaSignature(gallery) {
    return Array.from(gallery.querySelectorAll('slideshow-slide'))
      .map((slide) => slide.querySelector('img')?.getAttribute('src')?.split('?')[0] ?? slide.getAttribute('slide-id') ?? '')
      .join('|');
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Little Bees: the designs of one jabla share the same photos and the variants have no photo of their own.
        // Re-rendering the gallery would send it back to slide 1 and fight the design the shopper just chose,
        // so keep the current gallery (and its slide) when nothing in it would change.
        if (!detail.resource?.featured_media && this.#mediaSignature(this) === this.#mediaSignature(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
